<template>
  <Page :fill="false">
    <!-- Header -->
    <div class="bg-background-card rounded-lg shadow-card p-6">
      <div class="flex items-center gap-4">
        <Button
          icon="fa-solid fa-arrow-left"
          severity="secondary"
          text
          rounded
          v-tooltip.top="'Back to Project Settings'"
          @click="goBack"
        />
        <div class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center shrink-0">
          <i class="fa-solid fa-plug text-lg" />
        </div>
        <div>
          <h1 class="text-2xl font-bold text-primary">DataX Integration</h1>
          <p class="text-sm text-muted">
            Connect this project to Promantus DataX and map its asset classes to your asset types.
          </p>
        </div>
      </div>
    </div>

    <!-- Access gate -->
    <div v-if="!canManage" class="bg-background-card rounded-lg shadow-card p-8 text-center">
      <i class="fa-solid fa-lock text-4xl text-surface-300 mb-3" />
      <p class="text-lg font-semibold text-muted mb-1">Access Restricted</p>
      <p class="text-sm text-muted">
        Only project owners and administrators can manage DataX integration settings.
      </p>
    </div>

    <template v-else>
      <!-- Connections -->
      <div class="bg-background-card rounded-lg shadow-card overflow-hidden">
        <div class="flex items-center justify-between gap-3 p-4 border-b border-surface-200 dark:border-surface-700">
          <div>
            <h2 class="text-lg font-semibold">Connections</h2>
            <p class="text-sm text-muted">
              Each connection points at one DataX project. API keys are stored encrypted and never shown again.
            </p>
          </div>
          <Button label="Add Connection" icon="fa-solid fa-plus" @click="openAddConnection" />
        </div>

        <DataTable
          :value="connections"
          :loading="isLoading"
          data-key="id"
          striped-rows
          class="p-datatable-sm"
        >
          <Column field="name" header="Name" sortable>
            <template #body="{ data }">
              <div class="flex items-center gap-2">
                <span class="font-medium">{{ data.name }}</span>
                <Tag v-if="data.isDefault" value="Default" severity="info" />
              </div>
            </template>
          </Column>
          <Column field="baseUrl" header="Base URL">
            <template #body="{ data }">
              <span class="font-mono text-xs break-all">{{ data.baseUrl }}</span>
            </template>
          </Column>
          <Column field="apiKeyHint" header="API Key" style="width: 130px">
            <template #body="{ data }">
              <span class="font-mono text-xs">{{ data.apiKeyHint }}</span>
            </template>
          </Column>
          <Column header="Last Tested" style="width: 200px">
            <template #body="{ data }">
              <div v-if="data.lastTestedAtUtc" class="flex items-center gap-2">
                <Tag
                  :value="data.lastTestStatus === 'Succeeded' ? 'OK' : 'Failed'"
                  :severity="data.lastTestStatus === 'Succeeded' ? 'success' : 'danger'"
                />
                <span class="text-xs text-muted">{{ formatDateTime(data.lastTestedAtUtc) }}</span>
              </div>
              <span v-else class="text-xs text-muted">Never tested</span>
            </template>
          </Column>
          <Column header="Actions" style="width: 150px">
            <template #body="{ data }">
              <div class="flex gap-1">
                <Button
                  icon="fa-solid fa-plug-circle-check"
                  severity="secondary"
                  size="small"
                  text
                  rounded
                  :loading="testingConnectionId === data.id"
                  v-tooltip.top="'Test connection'"
                  @click="onTestStored(data)"
                />
                <Button
                  icon="fa-solid fa-pen"
                  severity="info"
                  size="small"
                  text
                  rounded
                  v-tooltip.top="'Edit'"
                  @click="openEditConnection(data)"
                />
                <Button
                  icon="fa-solid fa-trash"
                  severity="danger"
                  size="small"
                  text
                  rounded
                  v-tooltip.top="'Remove'"
                  @click="confirmDeleteConnection(data)"
                />
              </div>
            </template>
          </Column>

          <template #empty>
            <div class="text-center py-12">
              <i class="fa-solid fa-plug text-5xl text-surface-300 mb-3" />
              <p class="text-lg font-semibold text-muted mb-2">No DataX connections yet</p>
              <p class="text-sm text-muted mb-4">
                Add the base URL and API key issued by Promantus to start importing P&amp;ID extractions.
              </p>
              <Button label="Add Connection" icon="fa-solid fa-plus" @click="openAddConnection" />
            </div>
          </template>
        </DataTable>
      </div>

      <!-- Asset type mappings -->
      <div class="bg-background-card rounded-lg shadow-card overflow-hidden">
        <div class="p-4 border-b border-surface-200 dark:border-surface-700">
          <h2 class="text-lg font-semibold">Asset Type Mappings</h2>
          <p class="text-sm text-muted">
            How DataX classes translate to this project's asset types. The import wizard fills this in as you
            confirm mappings, and pre-selects them on later drawings.
          </p>
        </div>

        <DataTable
          :value="assetTypeMappings"
          :loading="isLoading"
          data-key="id"
          striped-rows
          paginator
          :rows="15"
          :rows-per-page-options="[15, 30, 50]"
          class="p-datatable-sm"
        >
          <Column field="dataXClass" header="DataX Class" sortable>
            <template #body="{ data }">
              <span class="font-medium">{{ data.dataXClass }}</span>
            </template>
          </Column>
          <Column header="ADAM Asset Type" sortable>
            <template #body="{ data }">
              <Select
                :model-value="data.assetTypeId"
                :options="assetTypes"
                option-label="label"
                option-value="id"
                :loading="isLoadingAssetTypes"
                class="w-full"
                filter
                @update:model-value="(value: string) => onRemapAssetType(data, value)"
              />
            </template>
          </Column>
          <Column header="Actions" style="width: 90px">
            <template #body="{ data }">
              <Button
                icon="fa-solid fa-trash"
                severity="danger"
                size="small"
                text
                rounded
                v-tooltip.top="'Remove mapping'"
                @click="confirmDeleteMapping(data)"
              />
            </template>
          </Column>

          <template #empty>
            <div class="text-center py-10">
              <i class="fa-solid fa-diagram-project text-4xl text-surface-300 mb-3" />
              <p class="text-base font-semibold text-muted mb-1">No mappings yet</p>
              <p class="text-sm text-muted">
                This list fills itself in as you map DataX classes during an import.
              </p>
            </div>
          </template>
        </DataTable>
      </div>
    </template>

    <DataXConnectionDialog
      ref="connectionDialog"
      v-model:visible="showConnectionDialog"
      :connection="editingConnection"
      :saving="isSaving"
      :testing="isTesting"
      @submit="onConnectionSubmit"
      @test="onDialogTest"
    />
  </Page>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import DataXConnectionDialog, {
  type DataXConnectionFormResult,
} from '@/features/project-administration/components/DataXConnectionDialog.vue';
import { useDataXSettings } from '@/features/project-administration/composables/useDataXSettings';
import { useUserRole } from '@/shared/composables/useUserRole';
import { AssetTypesService, type AssetTypeDto } from '@/shared/services/assetTypes';
import type { DataXAssetTypeMappingDto, DataXConnectionDto } from '@/shared/services/dataX';
import { ProjectRole, SystemRole } from '@/core/types/models/RoleTypes';

definePage({ meta: { requiresProject: true } });

const router = useRouter();
const confirm = useConfirm();
const { userRole } = useUserRole();

const {
  connections,
  assetTypeMappings,
  isLoading,
  isSaving,
  isTesting,
  load,
  createConnection,
  updateConnection,
  deleteConnection,
  updateAssetTypeMapping,
  deleteAssetTypeMapping,
  testStoredConnection,
  testUnsavedConnection,
} = useDataXSettings();

const showConnectionDialog = ref(false);
const editingConnection = ref<DataXConnectionDto | null>(null);
const testingConnectionId = ref<string | null>(null);
const connectionDialog = ref<InstanceType<typeof DataXConnectionDialog> | null>(null);

const assetTypeOptions = ref<AssetTypeDto[]>([]);
const isLoadingAssetTypes = ref(false);

/**
 * Mirrors the nav item's gating so a direct URL cannot bypass the menu-level restriction.
 */
const canManage = computed<boolean>(
  () => userRole.value === SystemRole.Administrator || userRole.value === ProjectRole.ProjectOwner
);

const assetTypes = computed(() =>
  assetTypeOptions.value.map((assetType) => ({
    id: assetType.id,
    label: assetType.description ? `${assetType.typeId} — ${assetType.description}` : assetType.typeId,
  }))
);

onMounted(async () => {
  if (!canManage.value) return;
  await Promise.all([load(), loadAssetTypes()]);
});

async function loadAssetTypes(): Promise<void> {
  isLoadingAssetTypes.value = true;
  try {
    assetTypeOptions.value = await AssetTypesService.getAll();
  } finally {
    isLoadingAssetTypes.value = false;
  }
}

function goBack(): void {
  router.push('/project-administration/project-settings');
}

function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

function openAddConnection(): void {
  editingConnection.value = null;
  showConnectionDialog.value = true;
}

function openEditConnection(connection: DataXConnectionDto): void {
  editingConnection.value = connection;
  showConnectionDialog.value = true;
}

async function onConnectionSubmit(result: DataXConnectionFormResult): Promise<void> {
  const ok = result.id
    ? await updateConnection(result.id, {
        name: result.name,
        baseUrl: result.baseUrl,
        // Null means "keep the stored key" — the server treats a missing apiKey that way.
        apiKey: result.apiKey,
        isDefault: result.isDefault,
      })
    : await createConnection({
        name: result.name,
        baseUrl: result.baseUrl,
        apiKey: result.apiKey ?? '',
        isDefault: result.isDefault,
      });

  if (ok) showConnectionDialog.value = false;
}

/**
 * Test from inside the dialog. Uses the stored-connection endpoint when editing without typing a
 * new key, so an existing connection can be verified without re-entering its credential.
 */
async function onDialogTest(payload: { baseUrl: string; apiKey: string; id: string | null }): Promise<void> {
  const result = payload.id && !payload.apiKey
    ? await testStoredConnection(payload.id)
    : await testUnsavedConnection(payload.baseUrl, payload.apiKey);

  connectionDialog.value?.setTestResult(result);
}

async function onTestStored(connection: DataXConnectionDto): Promise<void> {
  testingConnectionId.value = connection.id ?? null;
  try {
    await testStoredConnection(connection.id as string);
  } finally {
    testingConnectionId.value = null;
  }
}

function confirmDeleteConnection(connection: DataXConnectionDto): void {
  confirm.require({
    message: `Remove the DataX connection "${connection.name}"? Imports using it will stop working.`,
    header: 'Remove Connection',
    icon: 'fa-solid fa-triangle-exclamation',
    acceptClass: 'p-button-danger',
    accept: () => deleteConnection(connection.id as string, connection.name ?? ''),
  });
}

function confirmDeleteMapping(mapping: DataXAssetTypeMappingDto): void {
  confirm.require({
    message: `Remove the mapping for "${mapping.dataXClass}"? It will need mapping again on the next import.`,
    header: 'Remove Mapping',
    icon: 'fa-solid fa-triangle-exclamation',
    acceptClass: 'p-button-danger',
    accept: () => deleteAssetTypeMapping(mapping.id as string, mapping.dataXClass ?? ''),
  });
}

async function onRemapAssetType(mapping: DataXAssetTypeMappingDto, assetTypeId: string): Promise<void> {
  if (!assetTypeId || assetTypeId === mapping.assetTypeId) return;
  await updateAssetTypeMapping(mapping.dataXClass as string, assetTypeId);
}
</script>
